/**
 * `<RoundAnalysisSlot>` — one interview ROUND's slice of the Interview
 * Analysis tab (per-round arc, backend migration 0124 `round_id`).
 *
 * The tab renders one of these per `jd.interview_rounds` round (plus an
 * "Unassigned" slot for legacy `round_id=null` analyses, and a single default
 * slot when a JD has no rounds). Each slot is the pre-existing interview-
 * analysis state machine — dropzone / paste / Teams import → generating →
 * analysis — but keyed by `round_id`, so every slot reads, polls, and mutates
 * INDEPENDENTLY:
 *
 *   - `useAnalysisPasteBox` (transcript/skip cache) is keyed on `roundId`.
 *   - `useInterviewAnalysis` reads round-scoped (`round_scoped=true` + the
 *     slot's `roundId`, or `round_id IS NULL` for the unassigned slot).
 *   - submit / paste / skip / Teams-import all thread `round_id`.
 *
 * The completed state is a COMPACT per-round summary (verdict pill + final
 * verdict line) with "Full analysis ›" expanding the EXISTING
 * `<AnalysisLoadedView>` (the four-card stack is reused verbatim, never
 * rebuilt).
 */
import { useCallback, useEffect, useMemo, useState, type JSX } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";

import { Spinner } from "@/components/common/Spinner";
import { useToastStore } from "@/stores/useToastStore";
import type { Candidate } from "@/hooks/queries/candidates/useCandidates";

import { AnalysisPasteBox } from "@/components/candidate-page/AnalysisPasteBox";
import { AnalysisSkippedState } from "@/components/candidate-page/AnalysisSkippedState";
import { RegenerateConfirmModal } from "@/components/candidate-page/RegenerateConfirmModal";
import { AnalysisLoadedView } from "@/components/candidate-page/analysis/AnalysisLoadedView";
import { TranscriptDropzone } from "@/components/candidate-page/tabs/TranscriptDropzone";
import { TeamsTranscriptPicker } from "@/components/candidate-page/tabs/TeamsTranscriptPicker";

import {
  useAnalysisPasteBox,
  type AnalysisPasteBoxRow,
} from "@/hooks/queries/interview-analysis/useAnalysisPasteBox";
import { useInterviewAnalysis } from "@/hooks/queries/interview-analysis/useInterviewAnalysis";
import { useAnalysisInnerState } from "@/hooks/queries/interview-analysis/useAnalysisInnerState";
import { useSkipAnalysis } from "@/hooks/queries/interview-analysis/useSkipAnalysis";
import { useSubmitTranscript } from "@/hooks/queries/interview-analysis/useSubmitTranscript";
import {
  useImportTeamsTranscript,
  type TeamsTranscriptRow,
} from "@/hooks/queries/interview-analysis/useTeamsTranscripts";
import {
  extractFinalVerdictSentence,
  extractVerdict,
  verdictColor,
} from "@/lib/interview-analysis/parsers";

interface JdSummary {
  id: string;
  name: string;
}

interface StageLike {
  id: string;
  name: string;
}

/** Slot analysed-status, lifted to the tab for the "X of N analyzed" read. */
export type SlotStatus = "empty" | "generating" | "analyzed" | "skipped";

interface RoundAnalysisSlotProps {
  candidate: Candidate;
  jdId: string | undefined;
  /** The candidate's current non-terminal pipeline stage (drives `stage_id`). */
  activeStage: StageLike | null;
  /** Round id — `null` for the legacy "Unassigned" / no-rounds default slot. */
  roundId: string | null;
  roundName: string;
  /** 1-based round number for the header badge; `null` for the unassigned slot. */
  roundNumber: number | null;
  jds: JdSummary[];
  isActive: boolean;
  teamsAvailable: boolean;
  /** Reports this slot's status up so the tab can render round progress. */
  onStatusChange: (roundKey: string, status: SlotStatus) => void;
}

type LocalView = "auto" | "editing" | "skip-confirm";

export function RoundAnalysisSlot({
  candidate,
  jdId,
  activeStage,
  roundId,
  roundName,
  roundNumber,
  jds,
  isActive,
  teamsAvailable,
  onStatusChange,
}: RoundAnalysisSlotProps): JSX.Element {
  const queryClient = useQueryClient();
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const addToast = useToastStore((s) => s.addToast);
  const roundKey = roundId ?? "unassigned";

  // Transcript/skip cache, keyed per-round (the mutation hooks seed it).
  const pasteBoxQ = useAnalysisPasteBox({
    candidateId: candidate.id,
    jdId,
    stageId: activeStage?.id,
    roundId,
    enabled: isActive,
  });
  const hasTranscript = !pasteBoxQ.isFetched || !!pasteBoxQ.data?.transcript;

  // Round-scoped analysis read — each slot polls its OWN post row.
  const analysisQ = useInterviewAnalysis({
    candidateId: candidate.id,
    type: "post",
    jdId,
    roundScoped: true,
    roundId,
    enabled: isActive && !!jdId && hasTranscript,
  });

  const submitMutation = useSubmitTranscript();
  const skipMutation = useSkipAnalysis();
  const teamsImportMutation = useImportTeamsTranscript();

  const [localView, setLocalView] = useState<LocalView>("auto");
  const [showPasteArea, setShowPasteArea] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [showTeamsPicker, setShowTeamsPicker] = useState(false);
  const [importingTranscriptId, setImportingTranscriptId] = useState<string | null>(
    null,
  );
  const [genBarJdId, setGenBarJdId] = useState<string | undefined>(jdId);

  const row: AnalysisPasteBoxRow | null = pasteBoxQ.data ?? null;
  const isPending =
    submitMutation.isPending ||
    skipMutation.isPending ||
    teamsImportMutation.isPending;

  const innerState = useAnalysisInnerState({
    analysisQuery: analysisQ,
    pasteBoxQuery: pasteBoxQ,
    submitPending: submitMutation.isPending || teamsImportMutation.isPending,
  });

  // Report status up for the tab's "X of N rounds analyzed" progress read.
  const reportedStatus: SlotStatus = row?.skipped
    ? "skipped"
    : innerState.kind === "analysis-loaded"
      ? "analyzed"
      : innerState.kind === "generating"
        ? "generating"
        : "empty";
  useEffect(() => {
    onStatusChange(roundKey, reportedStatus);
  }, [onStatusChange, roundKey, reportedStatus]);

  const writeCache = useCallback(
    (next: AnalysisPasteBoxRow) => {
      if (!tenantSlug || !activeStage) return;
      queryClient.setQueryData(
        [
          "interview-analysis",
          "paste-box",
          tenantSlug,
          candidate.id,
          jdId,
          activeStage.id,
          roundId,
        ],
        next,
      );
    },
    [queryClient, tenantSlug, candidate.id, jdId, activeStage, roundId],
  );

  const handlePasteSubmit = useCallback(
    (transcript: string): void => {
      if (!jdId || !activeStage) return;
      submitMutation.mutate(
        {
          mode: "text",
          candidate_id: candidate.id,
          jd_id: jdId,
          stage_id: activeStage.id,
          round_id: roundId,
          transcript,
        },
        {
          onSuccess: (saved) => {
            writeCache(saved);
            setLocalView("auto");
            setShowPasteArea(false);
          },
          onError: () => {
            addToast("Failed to submit transcript. Please try again.", "error");
          },
        },
      );
    },
    [candidate.id, jdId, activeStage, roundId, submitMutation, writeCache, addToast],
  );

  const handleFileUpload = useCallback(
    (file: File): void => {
      if (!jdId || !activeStage) return;
      submitMutation.mutate(
        {
          mode: "file",
          candidate_id: candidate.id,
          jd_id: jdId,
          stage_id: activeStage.id,
          round_id: roundId,
          file,
        },
        {
          onSuccess: (saved) => {
            writeCache(saved);
            setLocalView("auto");
            setShowPasteArea(false);
          },
        },
      );
    },
    [candidate.id, jdId, activeStage, roundId, submitMutation, writeCache],
  );

  const handleFileReject = useCallback(
    (reason: string): void => {
      addToast(reason, "warning");
    },
    [addToast],
  );

  const handleTeamsImport = useCallback(
    (picked: TeamsTranscriptRow): void => {
      if (!jdId || !activeStage) return;
      setImportingTranscriptId(picked.transcript_id);
      teamsImportMutation.mutate(
        {
          candidate_id: candidate.id,
          jd_id: jdId,
          stage_id: activeStage.id,
          round_id: roundId,
          meeting_id: picked.meeting_id,
          transcript_id: picked.transcript_id,
        },
        {
          onSuccess: (saved) => {
            writeCache(saved);
            setImportingTranscriptId(null);
            setShowTeamsPicker(false);
            setLocalView("auto");
            setShowPasteArea(false);
          },
          onError: () => {
            setImportingTranscriptId(null);
          },
        },
      );
    },
    [candidate.id, jdId, activeStage, roundId, teamsImportMutation, writeCache],
  );

  const handleSkipConfirm = useCallback(() => {
    if (!jdId || !activeStage) return;
    skipMutation.mutate(
      {
        candidate_id: candidate.id,
        jd_id: jdId,
        stage_id: activeStage.id,
        round_id: roundId,
      },
      {
        onSuccess: (skipped) => {
          writeCache(skipped);
          setLocalView("auto");
        },
      },
    );
  }, [candidate.id, jdId, activeStage, roundId, skipMutation, writeCache]);

  const badge =
    roundNumber !== null ? `Round ${roundNumber}` : "Unassigned";

  const statusPill = useMemo(() => {
    if (row?.skipped) return <StatusPill label="Skipped" tone="muted" />;
    if (innerState.kind === "analysis-loaded") {
      const verdict = extractVerdict(innerState.row.analysis);
      return (
        <StatusPill
          label={verdict ?? "Analyzed"}
          color={verdict ? verdictColor(verdict) : undefined}
          tone={verdict ? "verdict" : "positive"}
        />
      );
    }
    if (innerState.kind === "generating")
      return <StatusPill label="Analyzing…" tone="accent" />;
    return <StatusPill label="Not started" tone="muted" />;
  }, [row?.skipped, innerState]);

  return (
    <section
      className="rounded-lg border border-border-subtle bg-surface-raised"
      data-testid="round-analysis-slot"
      data-round-id={roundId ?? "unassigned"}
    >
      <header className="flex items-center justify-between gap-3 border-b border-border-subtle px-4 py-3">
        <div className="flex min-w-0 flex-col">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-text-tertiary">
            {badge}
          </span>
          <span className="truncate text-sm font-semibold text-text-primary">
            {roundName}
          </span>
        </div>
        {statusPill}
      </header>

      <div className="px-4 py-3">{renderBody()}</div>

      {showTeamsPicker ? (
        <TeamsTranscriptPicker
          onImport={handleTeamsImport}
          onClose={() => {
            setShowTeamsPicker(false);
            setImportingTranscriptId(null);
            teamsImportMutation.reset();
          }}
          importingTranscriptId={importingTranscriptId}
          importError={teamsImportMutation.error ?? null}
        />
      ) : null}
    </section>
  );

  function renderBody(): JSX.Element {
    const stageName = activeStage?.name ?? roundName;

    if (localView === "editing" && activeStage) {
      return (
        <AnalysisPasteBox
          stageName={stageName}
          candidateName={candidate.name}
          initialValue={row?.transcript ?? ""}
          isPending={isPending}
          onSave={handlePasteSubmit}
          onSkip={() => setLocalView("skip-confirm")}
        />
      );
    }

    if (row?.skipped) {
      return (
        <AnalysisSkippedState
          stageName={roundName}
          onAddItNow={() => setLocalView("editing")}
        />
      );
    }

    if (innerState.kind === "analysis-loaded") {
      const verdictLine = extractFinalVerdictSentence(innerState.row.analysis);
      if (!expanded) {
        return (
          <div className="flex flex-col gap-2" data-testid="round-analysis-summary">
            {verdictLine ? (
              <p className="text-sm text-text-secondary">{verdictLine}</p>
            ) : (
              <p className="text-sm text-text-secondary">
                Interview analysis ready.
              </p>
            )}
            <button
              type="button"
              className="self-start text-sm font-medium text-brand-teal underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent-secondary)]/40"
              onClick={() => setExpanded(true)}
              data-testid="round-full-analysis-toggle"
            >
              Full analysis ›
            </button>
          </div>
        );
      }
      return (
        <div className="flex flex-col gap-2">
          <button
            type="button"
            className="self-start text-sm font-medium text-text-secondary hover:text-text-primary focus-visible:outline-none"
            onClick={() => setExpanded(false)}
            data-testid="round-collapse-analysis-toggle"
          >
            ‹ Collapse
          </button>
          <AnalysisLoadedView
            analysis={innerState.row.analysis}
            transcript={innerState.row.transcript}
            analysisId={innerState.row.id}
            modelUsed={innerState.row.modelUsed}
            jdId={genBarJdId ?? jdId}
            jds={jds}
            onJdChange={setGenBarJdId}
            onReplaceTranscript={activeStage ? () => setLocalView("editing") : undefined}
            isPending={isPending}
          />
        </div>
      );
    }

    if (innerState.kind === "loading") {
      return (
        <div
          className="flex items-center gap-2 py-4"
          data-testid="round-analysis-loading"
        >
          <Spinner size="sm" />
          <span className="text-sm text-text-secondary">Loading…</span>
        </div>
      );
    }

    if (innerState.kind === "generating") {
      return (
        <div
          className="flex items-center gap-2 py-4"
          data-testid="round-analysis-generating"
          role="status"
          aria-live="polite"
        >
          <Spinner size="sm" className="text-accent-secondary" />
          <span className="text-sm text-text-secondary">Analyzing interview…</span>
        </div>
      );
    }

    // Empty — no transcript, no analysis.
    if (!activeStage) {
      return (
        <p
          className="py-2 text-sm text-text-secondary"
          data-testid="round-no-active-stage"
        >
          Once {candidate.name.split(" ")[0] ?? candidate.name} reaches an active
          interview stage you can record this round&apos;s analysis here.
        </p>
      );
    }

    return (
      <div className="flex flex-col gap-3" data-testid="round-analysis-empty">
        <TranscriptDropzone
          onFile={handleFileUpload}
          onReject={handleFileReject}
          disabled={isPending}
        />
        {!showPasteArea ? (
          <p className="text-center text-sm text-text-secondary">
            or{" "}
            <button
              type="button"
              className="text-brand-teal underline underline-offset-2 hover:text-brand-teal/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent-secondary)]/40"
              onClick={() => setShowPasteArea(true)}
              data-testid="round-paste-text-toggle"
            >
              paste text instead
            </button>
            {/* Teams / auto-load lives beside paste — teal = the auto signal.
                Available → the picker; not-connected → a muted fallback. */}
            {teamsAvailable ? (
              <>
                {" "}
                or{" "}
                <button
                  type="button"
                  className="text-brand-teal underline underline-offset-2 hover:text-brand-teal/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent-secondary)]/40"
                  onClick={() => setShowTeamsPicker(true)}
                  disabled={isPending}
                  data-testid="round-teams-import-toggle"
                >
                  auto-load from Teams
                </button>
              </>
            ) : (
              <>
                {" · "}
                <span
                  className="text-text-tertiary"
                  data-testid="round-teams-connect-fallback"
                >
                  connect Teams to auto-load
                </span>
              </>
            )}
          </p>
        ) : (
          <AnalysisPasteBox
            stageName={stageName}
            candidateName={candidate.name}
            initialValue=""
            isPending={isPending}
            onSave={handlePasteSubmit}
            onSkip={() => setLocalView("skip-confirm")}
          />
        )}

        {localView === "skip-confirm" ? (
          <RegenerateConfirmModal
            title={`Skip the ${roundName} analysis?`}
            body="The next round's prep won't be enriched by this round."
            confirmLabel="Skip"
            isPending={skipMutation.isPending}
            onConfirm={handleSkipConfirm}
            onCancel={() => setLocalView("auto")}
          />
        ) : null}
      </div>
    );
  }
}

// ── Status pill ─────────────────────────────────────────────────────────────

interface StatusPillProps {
  label: string;
  tone: "muted" | "positive" | "accent" | "verdict";
  color?: string;
}

function StatusPill({ label, tone, color }: StatusPillProps): JSX.Element {
  const toneClass =
    tone === "positive"
      ? "bg-verdict-positive-bg text-verdict-positive-fg"
      : tone === "accent"
        ? "bg-accent-secondary/10 text-accent-secondary"
        : "bg-surface-sunken text-text-tertiary";
  return (
    <span
      className={`shrink-0 rounded-full px-2.5 py-0.5 text-xs font-semibold ${
        tone === "verdict" ? "bg-surface-sunken" : toneClass
      }`}
      style={tone === "verdict" && color ? { color } : undefined}
      data-testid="round-status-pill"
    >
      {label}
    </span>
  );
}
